import React, { useEffect, useState } from 'react';
import { cssColorValue, semanticColors, spacer16, spacer20, spacer40, Type } from '@spotify-internal/encore-web';
import { captureException } from '@sentry/nextjs';
import styled from 'styled-components';

import { useTranslation } from 'providers/translations/TranslationProvider';
import { User } from 'types';
import UserProfile from 'components/Greetings/UserProfile';
import SkeletonLoading from 'components/SkeletonLoading';
import { UbiHomePage } from 'utils/ubi/pages/useUbiHome';
import { LOGIN_REQUIRED } from 'utils/client/auth/errorMessages';

const Uppercase = styled(Type)`
  text-transform: uppercase;
`;

const Greeting = styled.div`
  margin: ${spacer20} 0;
  height: 48px;
`;

const LoginSuggestionType = styled(Type)`
  a {
    color: ${cssColorValue(semanticColors.textBase)};
  }
`;

const getLoginUrl = () => {
  const loginUrl = 'https://accounts.spotify.com/login/';
  return `${loginUrl}?continue=${encodeURIComponent(window.location.href)}`;
};

interface GreetingsProps {
  ubi: UbiHomePage;
}

const Greetings = ({ ubi }: GreetingsProps) => {
  const { t, traw } = useTranslation();
  const [isFetchingUser, setIsFetchingUser] = useState<boolean>(true);
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        // Inline import since the auth lib only works in browsers
        const auth = (await import('utils/client/auth/auth')).default;
        const response = await auth.fetch('https://api.spotify.com/v1/me', {});

        // We need to check if the response is ok for status 404 and 500 because fetch doesn't throw an exception.
        if (!response.ok) {
          throw new Error(`Problem fetching "/v1/me": ${response.status.toString()}`);
        }

        const userFetched: User = await response.json();
        setUser(userFetched);
      } catch (e) {
        // Exclude exception in sentry when user is not logged in
        if (e.error !== LOGIN_REQUIRED) captureException(e);
      }

      setIsFetchingUser(false);
    };

    fetchUser();
  }, []);

  const suggestToLogin = !(isFetchingUser || user);

  useEffect(() => {
    if (suggestToLogin && !isFetchingUser) {
      ubi.logLoginButtonImpression();
    }
  }, [isFetchingUser, suggestToLogin, ubi]);

  return (
    <>
      <Uppercase forwardedAs="h1" variant="cello" semanticColor="textSubdued" paddingBottom={spacer16}>
        {t('spotify_support')}
      </Uppercase>

      <Type as="h2" variant="forte" paddingBottom={spacer40}>
        {/* Change key of translation since it's part of a new context */}
        {t('search_title')}
      </Type>

      <Greeting>
        {user && <UserProfile user={user} />}
        {isFetchingUser && <SkeletonLoading width={200} height={48} />}
        {suggestToLogin && (
          <LoginSuggestionType variant="canon" semanticColor="textSubdued">
            {traw('home_login_prompt', { url: getLoginUrl() }, event => {
              const target = event.target as Element;
              if (target.nodeName.toLowerCase() === 'a') {
                ubi.logLoginButtonHit(getLoginUrl());
              }
            })}
          </LoginSuggestionType>
        )}
      </Greeting>
    </>
  );
};

export default Greetings;
